/**
 * Tags chip selector for the Binom Campaign tab. Selected tags render as
 * removable chips; typing filters available tags from /binom/tags and Enter
 * commits — creating a new tag if it does not exist (Binom auto-creates).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, KeyboardEvent, useMemo, useState } from 'react';
import { X } from 'lucide-react';

import { Input } from '@/components/ui/form/input/Input';
import { useBinomTags } from '@/hooks/binom';

import BinomTagChipList from './BinomTagChipList';

export interface BinomTagsFieldProps {
  value: Array<string | number>;
  onChange: (next: Array<string | number>) => void;
}

const BinomTagsField: FC<BinomTagsFieldProps> = ({ value, onChange }) => {
  const tagsQuery = useBinomTags();
  const [input, setInput] = useState('');

  const selectedSet = useMemo(
    () => new Set(value.map((v) => String(v).toLowerCase())),
    [value],
  );
  const suggestions = useMemo(() => {
    const all = Array.isArray(tagsQuery.data) ? tagsQuery.data : [];
    const lower = input.trim().toLowerCase();
    return all
      .filter((t) => !selectedSet.has(t.name.toLowerCase()))
      .filter((t) => !lower || t.name.toLowerCase().includes(lower))
      .slice(0, 8);
  }, [tagsQuery.data, input, selectedSet]);

  const add = (raw: string) => {
    const v = raw.trim();
    if (!v || selectedSet.has(v.toLowerCase())) return;
    onChange([...value, v]);
    setInput('');
  };

  const remove = (idx: number) => onChange(value.filter((_, i) => i !== idx));

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      add(input);
    } else if (e.key === 'Backspace' && !input && value.length > 0) {
      remove(value.length - 1);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1 rounded-md border border-input bg-background px-1.5 py-1">
        {value.map((tag, i) => (
          <span
            key={`${tag}-${i}`}
            className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[11px]"
          >
            {String(tag)}
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-muted-foreground hover:text-foreground"
              aria-label={`Remove ${tag}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder={value.length === 0 ? 'Type a tag…' : ''}
          className="h-6 min-w-[6rem] flex-1 border-0 px-1 text-xs focus-visible:ring-0"
        />
      </div>
      <BinomTagChipList suggestions={suggestions} onPick={add} />
    </div>
  );
};

export default BinomTagsField;
